import React, {
  ReactNode,
  startTransition,
  useEffect,
  useRef,
  useState,
} from "react";
import { SushiShopLogoSvg } from "../../Assets/images";
import FlexBox from "../../Components/FlexBox/FlexBox";
import NavigationMenu from "../NavigationMenu/NavigationMenu";
import {
  _HeaderBar,
  _HeaderBottomContainer,
  _HeaderContainer,
  _HeaderFlex,
  _HeaderH1,
} from "./Header.styled";
import useBreakpoint from "../../Components/Grid/hooks/useBreakpoint";
import {
  IMenuContentProps,
  MenuContentDrawer,
} from "../../Components/DrawerContent/MenuContent/MenuContent";
import TopBar from "../TopBar/TopBar";
import { DELIVERY_OPTIONS_TRANSITION_DURATION } from "../../Components/DeliveryOptionsStatus/DeliveryOptionsStatus.styled";

export interface IHeaderProps {
  isHome?: boolean;
  leftContent?: ReactNode | ((props: { fixMobileClass: string }) => ReactNode);
  middleContent?: ReactNode;
  rightContent?: ReactNode;
  onClickMenu?: () => void;
  onClickLogo?: () => void;
  menuProps?: IMenuContentProps;
  renderMenu?: () => ReactNode;
  topBarContent?: ReactNode;
  showDeliveryOptions?: boolean;
  isDeliveryOptionsDown?: boolean;
  showTopBar?: boolean;
  className?: string;
  style?: React.CSSProperties;
  autoHeight?: boolean;
  headerBottomContent?: (props: {
    style: React.CSSProperties;
    autoHeight?: boolean;
    isMobile?: boolean;
  }) => ReactNode;
}

const defaultRenderMenu = (
  menuProps?: IMenuContentProps,
  onClickMenu?: () => void
) => {
  return (
    <MenuContentDrawer {...menuProps}>
      {({ onOpen }) => (
        <NavigationMenu
          onClick={() => {
            onOpen();
            onClickMenu && onClickMenu();
          }}
          icon="menu"
        />
      )}
    </MenuContentDrawer>
  );
};

const Header = (props: IHeaderProps) => {
  const {
    isHome = false,
    leftContent,
    middleContent,
    rightContent,
    menuProps,
    onClickMenu,
    onClickLogo,
    renderMenu,
    topBarContent,
    showTopBar,
    showDeliveryOptions,
    isDeliveryOptionsDown = true,
    className,
    style,
    autoHeight,
    headerBottomContent,
  } = props;
  const { isMobile } = useBreakpoint();
  // const [topbarHeight, setTopbarHeight] = useState<number>(46);
  const displayTopBar = showTopBar && !!topBarContent;
  const logo = (
    <img
      src={SushiShopLogoSvg}
      style={{ cursor: "pointer" }}
      onClick={onClickLogo}
      loading="lazy"
      alt=""
    />
  );
  const [showDeliveryOptionsMobile, setShowDeliveryOptionsMobile] =
    React.useState(true);
  useEffect(() => {
    startTransition(() => {
      setShowDeliveryOptionsMobile(isMobile);
    });
  }, [isMobile]);

  // Debounce animation state to prevent rapid oscillations
  const [debouncedIsDeliveryOptionsDown, setDebouncedIsDeliveryOptionsDown] =
    useState(isDeliveryOptionsDown);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any pending timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce: wait for value to stabilize for 150ms before updating animation
    // This prevents rapid oscillations during scroll
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedIsDeliveryOptionsDown(isDeliveryOptionsDown);
    }, 350);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [isDeliveryOptionsDown]);

  const styleHeader: React.CSSProperties = {
    transform: `translate3d(0, ${
      debouncedIsDeliveryOptionsDown ? 0 : -64
    }px, 0)`,
    WebkitTransform: `translate3d(0, ${
      debouncedIsDeliveryOptionsDown ? 0 : -64
    }px, 0)`,
    transition: `transform ${DELIVERY_OPTIONS_TRANSITION_DURATION} ease-in-out`,
    willChange: "transform",
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
  };

  return (
    <>
      <TopBar visible={displayTopBar} style={{ zIndex: 41 }}>
        {topBarContent}
      </TopBar>
      <_HeaderContainer
        // $top={displayTopBar ? topbarHeight : 0}
        className={className}
        style={style}
        $autoHeight={autoHeight}
      >
        <_HeaderBar>
          <_HeaderFlex className="header-wrapper">
            <FlexBox
              className="header-left-container"
              gap={24}
              align="center"
              justify="flex-start"
              maxWidth={"40%"}
            >
              {isHome ? <_HeaderH1>{logo}</_HeaderH1> : logo}
              {typeof leftContent === "function"
                ? leftContent({
                    fixMobileClass: "float-left",
                  })
                : leftContent}
            </FlexBox>
            {!isMobile && (
              <FlexBox
                className="header-middle-container"
                gap={8}
                justify="center"
                maxWidth={"40%"}
              >
                {middleContent}
              </FlexBox>
            )}
            <FlexBox
              className="header-right-container"
              gap={24}
              justify="flex-end"
              maxWidth={"40%"}
            >
              {rightContent}
              {renderMenu
                ? renderMenu()
                : defaultRenderMenu(menuProps, onClickMenu)}
            </FlexBox>
          </_HeaderFlex>
        </_HeaderBar>
        {showDeliveryOptionsMobile && !!middleContent && (
          <FlexBox
            className="header-mobile-delivery-options"
            justify={"center"}
            align={"flex-start"}
            style={{
              position: "sticky",
              top: 64,
              maxHeight: autoHeight ? undefined : 0,
              height: autoHeight ? undefined : 0,
              zIndex: 41, //showDeliveryOptions ? 41 : -1000,
              ...styleHeader,
              // @ts-ignore
              "--header-mobile-delivery-options-height": showDeliveryOptions
                ? "64px"
                : "0",
            }}
          >
            {middleContent}
          </FlexBox>
        )}
        <_HeaderBottomContainer
          id="header-bottom-container"
          style={styleHeader}
        ></_HeaderBottomContainer>
        {headerBottomContent?.({ style: styleHeader, autoHeight, isMobile })}
      </_HeaderContainer>
    </>
  );
};

export default React.memo(Header);
